// eslint-disable-next-line jira/filename-case
import React from 'react';
import { render } from 'react-dom';
import { createRoot, type Root } from 'react-dom/client';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { StrictMode } from '@atlassian/jira-react-strict-mode/src/StrictMode.tsx';
import { lazyForPaint, lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { FlagsBoundary } from '@jira/platform__flags-boundary/src/FlagsBoundary.tsx';
import { JiraContextContainer } from '@jira/platform__jira-context-container/src/JiraContextContainer.tsx';

const reactRoots = new WeakMap<Element, Root>();

/** Mounts `element` into `mountPoint`: uses the React 18/19 `createRoot` API when `nike_r19_render_unmount_2` is on, else the legacy render path. */
const renderToMountPoint = (element: React.ReactElement, mountPoint: Element) => {
	if (fg('nike_r19_render_unmount_2')) {
		let root = reactRoots.get(mountPoint);

		if (!root) {
			root = createRoot(mountPoint);
			reactRoots.set(mountPoint, root);
		}

		root.render(element);
	} else {
		render(element, mountPoint);
	}
};

const throwError = (message: string) => {
	const error = new Error(message);
	log.safeErrorWithoutCustomerData('flags.entry', message, error);
	throw error;
};

const getContainer = () => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	let container = document.getElementById('flags');
	if (container == null) {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		container = document.createElement('div');
		container.id = 'flags';

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (document.body) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			document.body.appendChild(container);
		} else {
			throwError('document.body was not found whilst trying to create the flags container');
		}
	}
	return container;
};

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const Flags = lazyForPaint(
	() => import(/* webpackChunkName: "async-flags" */ '@atlassian/jira-flag-app/src/main.tsx'),
	{ ssr: false },
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const AfterPaintFlags = lazyAfterPaint(
	() =>
		// eslint-disable-next-line jira/import/webpack-magic-comment-entries
		import(/* webpackChunkName: "async-flags" */ '@atlassian/jira-flag-app/src/main.tsx'),
	{ ssr: false },
);

export const renderFlags = (): void =>
	renderToMountPoint(
		<StrictMode>
			<JiraContextContainer>
				<FlagsBoundary packageName="flags-entry">
					{__SPA__ ? <AfterPaintFlags /> : <Flags />}
				</FlagsBoundary>
			</JiraContextContainer>
		</StrictMode>,
		getContainer(),
	);
